import { useCallback, useMemo, useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { withScreen } from './Screen';
import { OrderFiltersBlock } from './OrderFiltersBlock';
import Button from './common/Button';
import { OrdersFilters, OrdersStatsPage_GetOrdersStatsQueryVariables, OrdersStatsPage_GetOrdersStatsQuery } from '../../codegen/graphql';
import { parseIntoURLSearchParams } from '../lib/parseIntoURLSearchParams';

const GET_ORDERS_STATS = gql`
  query OrdersStatsPage_GetOrdersStats($filters: OrdersFilters) {
    ordersStats(filters: $filters) {
      summary {
        ordersCount
        ordersWithUnconfirmedTransactionsCount
        ordersWithDataIntegrityErrorsCount
        grossSaleSum
        channelFeesSum
        productCostsSum
        shippingCostsSum
        vatSum
        netMarginSum
        grossMarginSum
      }
      orders {
        id
        orderId
        shopType
        netMargin
        lineItems {
          id
          quantity
          title
          imageUrl
          attributes {
            type
            value
          }
        }
      }
    }
  }
`;

function OrdersStatsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentFilters: OrdersFilters = useMemo(() => ({
    search: searchParams.get('search'),
    startDate: searchParams.get('startDate'),
    endDate: searchParams.get('endDate'),
    countryCode: searchParams.get('countryCode'),
    adId: searchParams.get('adId'),
    variationId: searchParams.get('variationId'),
  }), [searchParams]);

  const [getOrdersStats, { data, loading, error, called }] = useLazyQuery<
    OrdersStatsPage_GetOrdersStatsQuery,
    OrdersStatsPage_GetOrdersStatsQueryVariables
  >(GET_ORDERS_STATS, {
    fetchPolicy: 'network-only',
  });

  const runStats = useCallback(() => {
    getOrdersStats({ variables: { filters: currentFilters } });
  }, [currentFilters]);

  const handleFiltersChange = useCallback((change: Partial<OrdersFilters>) => {
    setSearchParams(parseIntoURLSearchParams(change));
  }, [setSearchParams]);

  const summary = data?.ordersStats?.summary;
  const orders = data?.ordersStats?.orders || [];

  return (
    <>
      <Helmet>
        <title>Orders Stats</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Orders Stats</h1>
        <p className="text-gray-600 mb-4">
          Confirm filters to compute server-side financial totals and per-order net margins.
        </p>

        <OrderFiltersBlock
          currentFilters={currentFilters}
          onFiltersChange={handleFiltersChange}
        />

        <div className="mb-6">
          <Button onClick={runStats} disabled={loading}>
            {loading ? 'Loading...' : 'Confirm'}
          </Button>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            {error.message}
          </div>
        )}

        {!called && (
          <div className="mb-6 rounded-lg border bg-white p-4 text-gray-600">
            Click Confirm to run the query with current filters.
          </div>
        )}

        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <SummaryCard label="Orders" value={summary.ordersCount} />
            <SummaryCard label="Orders with unconfirmed transactions" value={summary.ordersWithUnconfirmedTransactionsCount} />
            <SummaryCard label="Orders with data integrity errors" value={summary.ordersWithDataIntegrityErrorsCount} />
            <SummaryCard label="Gross sale sum" value={formatCurrency(summary.grossSaleSum)} />
            <SummaryCard label="Channel fees sum" value={formatCurrency(summary.channelFeesSum)} />
            <SummaryCard label="Product costs sum" value={formatCurrency(summary.productCostsSum)} />
            <SummaryCard label="Shipping costs sum" value={formatCurrency(summary.shippingCostsSum)} />
            <SummaryCard label="VAT sum" value={formatCurrency(summary.vatSum)} />
            <SummaryCard label="Net margin sum" value={formatCurrency(summary.netMarginSum)} />
            <SummaryCard label="Gross margin sum" value={formatCurrency(summary.grossMarginSum)} />
          </div>
        )}

        {summary && (
          <div className="overflow-x-auto bg-white rounded-lg shadow-sm border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order Line Items
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Margin
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="px-4 py-6 text-center text-gray-500">
                      No orders for current filters.
                    </td>
                  </tr>
                ) : (
                  orders.map((order: any) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 align-top">
                        <div className="mb-2 text-sm text-gray-700">
                          <Link to={`/orders/${order.id}`} className="font-medium text-blue-600 hover:underline">
                            {order.shopType} #{order.orderId || order.id}
                          </Link>
                        </div>

                        <div className="space-y-2">
                          {order.lineItems.map((lineItem: any) => (
                            <div key={lineItem.id} className="flex items-start gap-2">
                              {lineItem.imageUrl && (
                                <img src={lineItem.imageUrl} alt={lineItem.title} className="w-10 h-10 rounded-md object-cover shrink-0" />
                              )}
                              <div className="text-sm">
                                <div className="font-medium text-gray-900">
                                  {lineItem.title} <span className="text-gray-500">x{lineItem.quantity}</span>
                                </div>
                                {lineItem.attributes.length > 0 && (
                                  <div className="text-gray-600">
                                    {lineItem.attributes.map((attribute: any) => `${attribute.type}: ${attribute.value}`).join(' / ')}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className={order.netMargin >= 0 ? 'font-semibold text-green-700' : 'font-semibold text-red-700'}>
                          {formatCurrency(order.netMargin)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function SummaryCard({ label, value }: { label: string, value: number | string }) {
  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
    </div>
  );
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
}

export default withScreen(OrdersStatsPage);
